// Seamless looping noise for the map soundscapes (pure: plain Float32Arrays, unit-tested in node).
// A looped buffer clicks when its last sample doesn't flow into its first. White noise hides that, but pink and brown are
// smooth, so each loop is generated a little longer and its tail is equal-power crossfaded over its head: the sample after
// the loop point is then the natural continuation of the one before it.
import type { NoiseColor } from './mapAudioDef';

/** small fast seeded PRNG (render-side only; the sim has its own Rng) */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const NOISE_RMS = 0.25;
const XFADE = 2048;

/** One channel of `n` samples of looping noise, RMS-normalised to NOISE_RMS, DC-free, seam-continuous. */
export function makeNoise(color: NoiseColor, n: number, rand: () => number): Float32Array {
  const m = n + XFADE, y = new Float32Array(m);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0, br = 0;
  for (let i = 0; i < m; i++) {
    const w = rand() * 2 - 1;
    if (color === 'white') y[i] = w;
    else if (color === 'pink') { // Paul Kellet's refined pink filter
      b0 = 0.99886 * b0 + w * 0.0555179; b1 = 0.99332 * b1 + w * 0.0750759; b2 = 0.969 * b2 + w * 0.153852;
      b3 = 0.8665 * b3 + w * 0.3104856; b4 = 0.55 * b4 + w * 0.5329522; b5 = -0.7616 * b5 - w * 0.016898;
      y[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362; b6 = w * 0.115926;
    } else { br = (br + 0.02 * w) / 1.02; y[i] = br; } // leaky integrator: red/brown
  }
  // remove DC (brown drifts), then crossfade the tail into the head
  let mean = 0; for (let i = 0; i < m; i++) mean += y[i]; mean /= m;
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) out[i] = y[i] - mean;
  for (let i = 0; i < XFADE; i++) {
    const u = (i + 0.5) / XFADE, a = Math.sin(u * Math.PI / 2), b = Math.cos(u * Math.PI / 2);
    out[i] = (y[i] - mean) * a + (y[n + i] - mean) * b;
  }
  let ss = 0; for (let i = 0; i < n; i++) ss += out[i] * out[i];
  const k = NOISE_RMS / Math.sqrt(ss / n || 1);
  for (let i = 0; i < n; i++) out[i] *= k;
  return out;
}

/** Seam check (QA + tests): the jump across the loop point relative to the typical sample-to-sample step. */
export function seamRatio(d: Float32Array): number {
  let s = 0; for (let i = 1; i < d.length; i++) s += Math.abs(d[i] - d[i - 1]);
  const mean = s / (d.length - 1);
  return Math.abs(d[0] - d[d.length - 1]) / (mean || 1);
}
